"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.customerListResponseSchema = exports.customerDetailSchema = exports.customerSummarySchema = exports.serviceLocationSchema = exports.updateServiceLocationRequestSchema = exports.addServiceLocationRequestSchema = exports.updateCustomerRequestSchema = exports.createCustomerRequestSchema = exports.serviceLocationInputSchema = exports.customerContactSchema = exports.updateCustomerContactRequestSchema = exports.createCustomerContactRequestSchema = exports.smsConsentStatusSchema = exports.communicationChannelSchema = exports.customerContactRoleSchema = exports.customerContactStatusSchema = exports.customerContactIdSchema = exports.customerIdSchema = exports.serviceLocationStatusSchema = exports.customerStatusSchema = exports.customerTypeSchema = void 0;
const zod_1 = require("zod");
const pagination_1 = require("./pagination");
const optionalTrimmedString = (maxLength) => zod_1.z
    .string()
    .trim()
    .max(maxLength)
    .optional()
    .transform((value) => value || undefined);
const optionalEmail = zod_1.z
    .string()
    .trim()
    .email()
    .max(320)
    .optional()
    .transform((value) => value || undefined);
const optionalPhone = zod_1.z
    .string()
    .trim()
    .regex(/^\+[1-9]\d{7,14}$/, "Phone must use E.164 format.")
    .optional()
    .transform((value) => value || undefined);
const nullableEmailPatch = zod_1.z
    .union([zod_1.z.string().trim().email().max(320), zod_1.z.literal(""), zod_1.z.null()])
    .optional()
    .transform((value) => (value === undefined ? undefined : value || null));
const nullablePhonePatch = zod_1.z
    .union([
    zod_1.z
        .string()
        .trim()
        .regex(/^\+[1-9]\d{7,14}$/, "Phone must use E.164 format."),
    zod_1.z.literal(""),
    zod_1.z.null(),
])
    .optional()
    .transform((value) => (value === undefined ? undefined : value || null));
const timeZoneSchema = zod_1.z.string().trim().refine(isValidTimeZone, {
    message: "A valid IANA timezone is required.",
});
exports.customerTypeSchema = zod_1.z.enum(["RESIDENTIAL", "COMMERCIAL"]);
exports.customerStatusSchema = zod_1.z.enum(["ACTIVE", "ARCHIVED"]);
exports.serviceLocationStatusSchema = zod_1.z.enum(["ACTIVE", "ARCHIVED"]);
exports.customerIdSchema = zod_1.z.string().uuid();
exports.customerContactIdSchema = zod_1.z.string().uuid();
exports.customerContactStatusSchema = zod_1.z.enum(["ACTIVE", "ARCHIVED"]);
exports.customerContactRoleSchema = zod_1.z.enum([
    "PRIMARY",
    "ESTIMATE_APPROVER",
    "BILLING",
    "ON_SITE",
]);
exports.communicationChannelSchema = zod_1.z.enum(["EMAIL", "SMS"]);
exports.smsConsentStatusSchema = zod_1.z.enum([
    "NOT_REQUESTED",
    "OPTED_IN",
    "OPTED_OUT",
]);
const customerContactFieldsSchema = zod_1.z
    .object({
    name: zod_1.z.string().trim().min(2).max(160),
    email: optionalEmail,
    phone: optionalPhone,
    roles: zod_1.z
        .array(exports.customerContactRoleSchema)
        .min(1)
        .max(4)
        .refine((roles) => new Set(roles).size === roles.length, {
        message: "Contact roles must be unique.",
    }),
    preferredChannel: exports.communicationChannelSchema,
    smsConsentStatus: exports.smsConsentStatusSchema.default("NOT_REQUESTED"),
})
    .superRefine((value, context) => {
    if (!value.email && !value.phone) {
        context.addIssue({
            code: "custom",
            path: ["email"],
            message: "An email or phone number is required.",
        });
    }
    if (value.preferredChannel === "EMAIL" && !value.email) {
        context.addIssue({
            code: "custom",
            path: ["preferredChannel"],
            message: "Email is required for the preferred channel.",
        });
    }
    if (value.preferredChannel === "SMS" && !value.phone) {
        context.addIssue({
            code: "custom",
            path: ["preferredChannel"],
            message: "A phone number is required for SMS.",
        });
    }
});
exports.createCustomerContactRequestSchema = customerContactFieldsSchema;
exports.updateCustomerContactRequestSchema = customerContactFieldsSchema;
exports.customerContactSchema = zod_1.z.object({
    id: exports.customerContactIdSchema,
    name: zod_1.z.string(),
    email: zod_1.z.string().email().nullable(),
    phone: zod_1.z.string().nullable(),
    roles: zod_1.z.array(exports.customerContactRoleSchema),
    preferredChannel: exports.communicationChannelSchema,
    smsConsentStatus: exports.smsConsentStatusSchema,
    smsConsentUpdatedAt: zod_1.z.string().datetime().nullable(),
    status: exports.customerContactStatusSchema,
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
});
exports.serviceLocationInputSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(2).max(120),
    addressLine1: zod_1.z.string().trim().min(3).max(160),
    addressLine2: optionalTrimmedString(160),
    city: zod_1.z.string().trim().min(2).max(100),
    state: zod_1.z
        .string()
        .trim()
        .transform((value) => value.toUpperCase())
        .pipe(zod_1.z.string().regex(/^[A-Z]{2}$/, "Use a two-letter US state code.")),
    postalCode: zod_1.z
        .string()
        .trim()
        .regex(/^\d{5}(?:-\d{4})?$/, "Use a valid US ZIP code."),
    timezone: timeZoneSchema,
});
exports.createCustomerRequestSchema = zod_1.z.object({
    customer: zod_1.z.object({
        type: exports.customerTypeSchema,
        displayName: zod_1.z.string().trim().min(2).max(160),
        email: optionalEmail,
        phone: optionalPhone,
    }),
    primaryLocation: exports.serviceLocationInputSchema,
});
exports.updateCustomerRequestSchema = zod_1.z
    .object({
    type: exports.customerTypeSchema.optional(),
    displayName: zod_1.z.string().trim().min(2).max(160).optional(),
    email: nullableEmailPatch,
    phone: nullablePhonePatch,
})
    .refine((value) => value.type !== undefined ||
    value.displayName !== undefined ||
    value.email !== undefined ||
    value.phone !== undefined, { message: "At least one customer field is required." });
exports.addServiceLocationRequestSchema = exports.serviceLocationInputSchema;
exports.updateServiceLocationRequestSchema = exports.serviceLocationInputSchema
    .extend({
    latitude: zod_1.z.number().min(-90).max(90).nullable(),
    longitude: zod_1.z.number().min(-180).max(180).nullable(),
})
    .refine((value) => (value.latitude === null) === (value.longitude === null), {
    path: ["latitude"],
    message: "Provide both map coordinates or neither.",
});
exports.serviceLocationSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    name: zod_1.z.string(),
    addressLine1: zod_1.z.string(),
    addressLine2: zod_1.z.string().nullable(),
    city: zod_1.z.string(),
    state: zod_1.z.string().length(2),
    postalCode: zod_1.z.string(),
    country: zod_1.z.literal("US"),
    timezone: zod_1.z.string(),
    isPrimary: zod_1.z.boolean(),
    status: exports.serviceLocationStatusSchema,
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
});
exports.customerSummarySchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    type: exports.customerTypeSchema,
    displayName: zod_1.z.string(),
    email: zod_1.z.string().email().nullable(),
    phone: zod_1.z.string().nullable(),
    status: exports.customerStatusSchema,
    primaryLocation: exports.serviceLocationSchema.nullable(),
    locationCount: zod_1.z.number().int().nonnegative(),
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
});
exports.customerDetailSchema = exports.customerSummarySchema
    .omit({ primaryLocation: true, locationCount: true })
    .extend({
    contacts: zod_1.z.array(exports.customerContactSchema),
    locations: zod_1.z.array(exports.serviceLocationSchema),
});
exports.customerListResponseSchema = zod_1.z.object({
    items: zod_1.z.array(exports.customerSummarySchema),
    total: zod_1.z.number().int().nonnegative(),
    pageInfo: pagination_1.pageInfoSchema.default(pagination_1.defaultPageInfo),
});
function isValidTimeZone(value) {
    try {
        new Intl.DateTimeFormat("en-US", { timeZone: value }).format();
        return true;
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=customer.js.map